import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BarChart, TrendingUp, AlertTriangle, Target } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const USRestockingArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-teal-500 text-white">
                <BarChart className="h-3 w-3 mr-1" />
                {t('article.restocking.badge')}
              </Badge>
              <span className="text-gray-500 text-sm">{t('article.restocking.date')}</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.restocking.title')}
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.restocking.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://imgs.newseditor.aws.kuehne-nagel.cloud/73464800-2114-46db-91cf-b352f759b37a.jpg"
              alt="Container shipping market outlook and US restocking analysis" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
                  {t('article.restocking.stat1')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">{t('article.restocking.statValue1')}</div>
                <p className="text-sm text-gray-600">{t('article.restocking.statDesc1')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Target className="h-5 w-5 mr-2 text-blue-600" />
                  {t('article.restocking.stat2')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('article.restocking.statValue2')}</div>
                <p className="text-sm text-gray-600">{t('article.restocking.statDesc2')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-red-600" />
                  Congestion Risk
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-red-600">Extremely</div>
                <p className="text-sm text-gray-600">High risk level</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('article.restocking.outlookTitle')}</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('article.restocking.outlookContent')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.restocking.potentialTitle')}</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              {t('article.restocking.potentialContent')}
            </p>
            <blockquote className="border-l-4 border-teal-500 pl-6 italic text-gray-700 mb-6">
              "{t('article.restocking.quoteContent')}"
              <footer className="text-sm font-semibold mt-2">{t('article.restocking.quoteFooter')}</footer>
            </blockquote>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('article.restocking.analysisTitle')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('article.restocking.driversTitle')}</h4>
              <ul className="space-y-2">
                <li><strong>{t('article.restocking.economicRecovery').split(':')[0]}:</strong> {t('article.restocking.economicRecovery').split(':')[1]}</li>
                <li><strong>{t('article.restocking.supplyChainResilience').split(':')[0]}:</strong> {t('article.restocking.supplyChainResilience').split(':')[1]}</li>
                <li><strong>{t('article.restocking.seasonalPrep').split(':')[0]}:</strong> {t('article.restocking.seasonalPrep').split(':')[1]}</li>
                <li><strong>{t('article.restocking.tradePolicyStability').split(':')[0]}:</strong> {t('article.restocking.tradePolicyStability').split(':')[1]}</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Red Sea Route Return Uncertainty</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The second major uncertainty centers on when carriers will resume Red Sea transits. 
              While security conditions remain the primary concern, the CEO warned of significant 
              operational challenges if multiple carriers return simultaneously to Suez Canal routes.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">European Port Congestion Risk</h3>
            <div className="bg-red-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Mass Return Scenario Risks:</h4>
              <ul className="space-y-2">
                <li><strong>Simultaneous Arrivals:</strong> Multiple carriers resuming Suez routes at once</li>
                <li><strong>Reduced Transit Times:</strong> Faster arrivals overwhelming port capacity</li>
                <li><strong>Scheduling Disruption:</strong> Compressed arrival windows creating bottlenecks</li>
                <li><strong>Infrastructure Strain:</strong> European ports unprepared for sudden volume surge</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Strategic Planning Challenges</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The combination of these uncertainties creates complex planning challenges for shipping 
              lines, port operators, and supply chain managers. Companies must prepare for multiple 
              scenarios while maintaining operational flexibility to respond to rapid market changes.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Demand Surge Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              A 4-5% boost in US demand would have cascading effects across the global shipping network:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>Capacity Utilization:</strong> Higher vessel utilization rates and potential shortages</li>
              <li><strong>Freight Rates:</strong> Upward pressure on container shipping rates</li>
              <li><strong>Service Frequency:</strong> Increased demand for regular shipping services</li>
              <li><strong>Equipment Availability:</strong> Container and chassis shortages in key markets</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Port Infrastructure Preparedness</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              European ports face the challenge of preparing for potential congestion while managing 
              current operations efficiently. The "extremely high" congestion risk identified by 
              Hapag-Lloyd's CEO suggests that ports may need to implement contingency measures and 
              coordinate closely with shipping lines on return timing.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Market Timing Considerations</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Critical Timing Factors:</h4>
              <ul className="space-y-2">
                <li><strong>Seasonal Patterns:</strong> Alignment with traditional shipping seasons</li>
                <li><strong>Economic Cycles:</strong> Correlation with US economic recovery timing</li>
                <li><strong>Security Developments:</strong> Red Sea situation stability assessment</li>
                <li><strong>Competitive Dynamics:</strong> Carrier coordination vs. competitive advantage</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Coordination Needs</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The CEO's warnings highlight the need for industry-wide coordination to manage both 
              opportunities and risks. This includes collaboration between carriers on Red Sea return 
              timing and coordination with port authorities to manage potential congestion scenarios.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Supply Chain Resilience</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Both uncertainties underscore the importance of supply chain resilience and flexibility. 
              Companies that can adapt quickly to changing demand patterns and route availability 
              will be better positioned to capitalize on opportunities while managing operational risks.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Investment Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The potential for significant demand increases and operational challenges suggests 
              continued investment needs in port infrastructure, vessel capacity, and digital 
              systems to manage complex logistics scenarios. Companies must balance immediate 
              operational needs with long-term strategic positioning.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Based on Hapag-Lloyd CEO Rolf Habben Jansen's analysis reported by Kuehne+Nagel maritime news. 
                <a href="https://mykn.kuehne-nagel.com/news/article/us-restocking-red-sea-return-the-two-big" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default USRestockingArticle;